// The dashboard's connection to the search.
//
// A socket that reconnects, with the HTTP endpoint as the first paint. The
// order matters: the fetch answers immediately with whatever the server is
// holding, so the page has content before the socket has finished its
// handshake — and on a page whose whole subject is a long-running background
// job, "connecting…" over an empty screen is indistinguishable from "nothing
// has ever run".
//
// **The last message wins, always.** There is no log to replay and no ordering
// to preserve: a digest is a complete picture of a thing that already happened,
// so a subscriber that connects halfway through is not behind and a dropped
// message costs nothing but freshness. That is the whole reason this is far
// simpler than `RoomClient` next door, where the log *is* the state.

import { useEffect, useRef, useState } from "react";

import {
  type TunerDigest,
  type TunerPulse,
  type TunerServerMessage,
  TUNER_API_PATH,
  tunerSocketUrl
} from "src/engine/net/tuner";

export type FeedStatus = "connecting" | "live" | "offline";

export interface Feed {
  digest: TunerDigest | null;
  /**
   * The search's liveness, on its own much faster clock.
   *
   * Null before the first one, which is also what a tuner running older code
   * looks like — so the page falls back to the digest's own age rather than
   * reporting a dead feed at a deployment that simply cannot send this.
   */
  pulse: TunerPulse | null;
  status: FeedStatus;
  /** When the last digest arrived here, which is not when it was built. */
  receivedAt: number;
}

/** Backoff bounds: quick enough to catch a redeploy, slow enough not to hammer. */
const RETRY_MS = 1000;
const RETRY_MAX_MS = 20_000;

export function useTunerFeed(): Feed {
  const [digest, setDigest] = useState<TunerDigest | null>(null);
  const [pulse, setPulse] = useState<TunerPulse | null>(null);
  const [status, setStatus] = useState<FeedStatus>("connecting");
  const [receivedAt, setReceivedAt] = useState(0);
  // Every timer and socket this hook owns, so the cleanup can be exhaustive.
  // A reconnecting socket that outlives its component reconnects forever.
  const mounted = useRef(true);
  const socket = useRef<WebSocket | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mounted.current = true;
    let backoff = RETRY_MS;

    const take = (next: TunerDigest | null): void => {
      if (!mounted.current || !next) return;
      setDigest((held) => {
        // Never go backwards. The fetch and the socket both deliver, and they
        // race — a slow fetch landing after the first socket message would
        // otherwise replace live data with what the server held a moment ago.
        if (held && held.at >= next.at) return held;
        return next;
      });
      setReceivedAt(Date.now());
    };

    const beat = (next: TunerPulse | null): void => {
      if (!mounted.current || !next) return;
      // Same "never go backwards" rule the digest takes, and for the same race:
      // the fetch and the socket both deliver one.
      setPulse((held) => (held && held.at >= next.at ? held : next));
    };

    // First paint, from the plain endpoint.
    void fetch(TUNER_API_PATH, { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { digest?: TunerDigest; pulse?: TunerPulse } | null) => {
        take(body?.digest ?? null);
        beat(body?.pulse ?? null);
      })
      .catch(() => {
        // No endpoint, or the gate refused. The socket is the real path and
        // reports its own failure; this one is only ever an accelerator.
      });

    const connect = (): void => {
      if (!mounted.current) return;
      let ws: WebSocket;
      try {
        ws = new WebSocket(tunerSocketUrl(window.location));
      } catch {
        // A malformed URL cannot be retried into working, but the page must
        // not be left claiming it is connecting.
        setStatus("offline");
        return;
      }
      socket.current = ws;

      ws.onopen = () => {
        if (!mounted.current) return;
        backoff = RETRY_MS;
        setStatus("live");
      };
      ws.onmessage = (event) => {
        let message: TunerServerMessage;
        try {
          message = JSON.parse(String(event.data)) as TunerServerMessage;
        } catch {
          return;
        }
        if (message.t === "digest") take(message.digest);
        if (message.t === "pulse") beat(message.pulse);
      };
      ws.onclose = () => {
        socket.current = null;
        if (!mounted.current) return;
        setStatus("offline");
        timer.current = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, RETRY_MAX_MS);
      };
      // `onclose` fires after `onerror`, so the reconnect is scheduled there
      // and this only stops the default console noise from being the only
      // record that anything went wrong.
      ws.onerror = () => {
        if (mounted.current) setStatus("offline");
      };
    };
    connect();

    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      // Closed by hand: an open socket whose component has gone is a leak that
      // keeps re-rendering nothing, and in StrictMode there are two of them.
      socket.current?.close();
      socket.current = null;
    };
  }, []);

  return { digest, pulse, status, receivedAt };
}
